import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Star, CheckCircle, Phone, Zap, Home as HomeIcon, Building, Thermometer, Building2, Settings, Bell, Sun, Battery } from 'lucide-react';
import { reviews, services, serviceAreas, galleryImages } from '../data/mock';

const Home = () => {

  const getServiceIcon = (iconName) => {
    const icons = {
      home: HomeIcon,
      building: Building,
      thermometer: Thermometer,
      zap: Zap,
      'building-2': Building2,
      settings: Settings,
      bell: Bell,
      sun: Sun,
      battery: Battery
    };
    const IconComponent = icons[iconName] || Zap;
    return <IconComponent className="h-8 w-8" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative text-white py-24 lg:py-32"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_se-power-solutions/artifacts/v8nqved6_90a441_2e3b77e059df45f592d3dfb08e785ef8~mv2.avif)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-blue-800/30 to-blue-900/40"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block bg-yellow-500 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Family Owned & Operated
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Reliable Electrical Services in London & Kingston, Ontario
            </h1>
            <div className="bg-yellow-500 text-gray-900 px-6 py-4 rounded-lg inline-block mb-6">
              <p className="text-2xl font-bold">⚡ Same Day & Next Day Services Available!</p>
            </div>
            <p className="text-xl mb-8 text-blue-100">
              Professional, trustworthy, and fully licensed electrical contractors. Emergency calls available evenings and weekends.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <a href="https://calendar.app.google/mAWkTnFLgHp2TVZp6" target="_blank" rel="noopener noreferrer">Book Online</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Rating Badges */}
      <section className="bg-white py-8 shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="flex items-center gap-2 bg-yellow-500 px-6 py-3 rounded-lg shadow-md">
              <span className="text-3xl">💳</span>
              <div className="text-sm">
                <div className="font-bold text-gray-900 text-lg">Financing Available!</div>
                <div className="text-gray-900 font-semibold">Affordable Payment Plans</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-blue-900 text-white px-4 py-2 rounded-lg font-bold text-xl">A+</div>
              <div className="text-sm">
                <div className="font-semibold">BBB Rating</div>
                <div className="text-gray-600">Better Business Bureau</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <div className="text-sm">
                <div className="font-semibold">5.0 Rating</div>
                <div className="text-gray-600">Google Reviews</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-10 w-10 text-green-600" />
              <div className="text-sm">
                <div className="font-semibold">Fully Licensed</div>
                <div className="text-gray-600">& Insured</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg font-bold text-lg">🏆</div>
              <div className="text-sm">
                <div className="font-semibold">CommunityVotes London 2024</div>
                <div className="text-gray-600">Winner - Best Electrician</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive electrical solutions for residential and commercial properties
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-shadow duration-300 border-2 hover:border-blue-900">
                <CardHeader>
                  <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                    {getServiceIcon(service.icon)}
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0 text-blue-900 font-semibold">
                    <Link to={service.link}>Learn More →</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Electrical Services You Can Trust
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Studerus Electric Inc. is a family-owned electrical contracting company serving London, Kingston, and surrounding areas in Ontario. With years of experience and a commitment to excellence, we provide reliable electrical solutions for homes and businesses.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Same & Next Day Services</h3>
                    <p className="text-gray-600">Fast turnaround for urgent electrical needs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Now Offering Financing!</h3>
                    <p className="text-gray-600">Affordable payment plans available for all projects</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Team of 17 Electricians</h3>
                    <p className="text-gray-600">Dedicated office staff available for your needs now</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Quality Service</h3>
                    <p className="text-gray-600">Experienced electricians delivering high-quality work on every project</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Safety First</h3>
                    <p className="text-gray-600">All work performed to code with safety as our top priority</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Customer Satisfaction</h3>
                    <p className="text-gray-600">Dedicated to providing excellent customer service and exceeding expectations</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800">
                  <Link to="/contact">Book Online</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:519-709-9242">Call 519-709-9242</a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_se-power-solutions/artifacts/p3kop7s6_20200225_101603.jpg"
                alt="Studerus Electric electrical panel installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Read reviews from our satisfied customers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review) => (
              <Card key={review.id} className="bg-white">
                <CardHeader>
                  <div className="flex mb-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{review.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{review.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild variant="outline" size="lg">
              <a href="https://www.google.com/search?q=studerus+electric" target="_blank" rel="noopener noreferrer">
                Read More Reviews
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Work</h2>
            <p className="text-xl text-gray-600">Quality electrical work across Ontario</p>
          </div>
          
          {/* Continuous Scrolling Gallery */}
          <div className="relative">
            <style>{`
              @keyframes scroll {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              
              .scroll-container {
                display: flex;
                animation: scroll 320s linear infinite;
                width: fit-content;
              }
              
              .scroll-container:hover {
                animation-play-state: running;
              }
              
              .gallery-item {
                flex: 0 0 350px;
                margin: 0 12px;
              }
            `}</style>
            
            <div className="flex overflow-hidden">
              <div className="scroll-container">
                {/* Original images */}
                {galleryImages.map((image) => (
                  <div key={`original-${image.id}`} className="gallery-item">
                    <div className="relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                ))}
                {/* Duplicate images for seamless loop */}
                {galleryImages.map((image) => (
                  <div key={`duplicate-${image.id}`} className="gallery-item">
                    <div className="relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Areas We Service</h2>
            <p className="text-xl text-blue-100">
              Providing quality electrical services throughout Ontario
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-center">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-blue-800 rounded-lg p-4 hover:bg-blue-700 transition-colors">
                <p className="font-semibold text-lg">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800 text-white">
              <Link to="/contact">Book Online</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;